import styled from 'styled-components';
const theme = {
    colors: {
        primary: 'var(--bg-primary)',
        secondary: 'var(--bg-secondary)',
        accent: 'var(--accent-color)',
        accentHover: 'var(--text-secondary)',
        accentDark: 'var(--text-tertiary)',
        text: {
            primary: 'var(--text-primary)',
            secondary: 'var(--text-secondary)',
            tertiary: 'var(--text-tertiary)',
            inverse: 'var(--bg-primary)',
            white: 'var(--text-primary)',
        },
        background: {
            primary: 'var(--bg-primary)',
            secondary: 'var(--bg-secondary)',
            tertiary: 'var(--bg-tertiary)',
            gradient: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
            overlay: 'var(--bg-primary)',
        },
        border: {
            primary: 'var(--border-color)',
            accent: 'var(--border-color-hover)',
            light: 'var(--border-color)',
        },
        success: 'var(--success-color)',
        error: 'var(--error-color)',
    },
    typography: {
        fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontSize: {
            xs: '12px',
            sm: '14px',
            base: '16px',
            lg: '18px',
            xl: '24px',
            '2xl': '32px',
            '3xl': '48px',
        },
        fontWeight: {
            light: 300,
            normal: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
        },
        letterSpacing: '0.5px',
        lineHeight: '1.6',
    },
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        '2xl': '48px',
        '3xl': '64px',
        '4xl': '80px',
    },
    effects: {
        transition: {
            fast: '0.2s ease',
            normal: '0.3s ease',
            luxury: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        boxShadow: {
            sm: '0 2px 4px rgba(0,0,0,0.1)',
            md: '0 4px 8px rgba(0,0,0,0.2)',
            lg: '0 8px 16px rgba(0,0,0,0.3)',
        },
    },
    breakpoints: {
        small: '375px',
        mobile: '768px',
        tablet: '1024px',
    },
};
export const PageContainer = styled.div `
  min-height: calc(100vh - 120px);
  padding: 48px 32px;
  max-width: 1600px;
  margin: 0 auto;
  color: ${theme.colors.text.primary};
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 1400px) {
    max-width: 1400px;
    padding: 48px 24px;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
    padding: 32px 20px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 32px 16px;
  }

  @media (max-width: ${theme.breakpoints.small}) {
    padding: 24px 12px;
    min-height: calc(100vh - 80px);
    max-width: 100vw;
  }

  /* Ensure all child elements respect container width */
  * {
    box-sizing: border-box;
  }
`;
export const PageTitle = styled.h1 `
  font-family: 'Adamina', serif;
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.normal};
  color: ${theme.colors.text.white};
  margin-bottom: ${theme.spacing.xl};
  text-transform: uppercase;
  letter-spacing: 3px;
  position: relative;
  line-height: 1.2;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, ${theme.colors.accent}, transparent);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.fontSize['2xl']};
    letter-spacing: 2px;
  }

  @media (max-width: ${theme.breakpoints.small}) {
    font-size: ${theme.typography.fontSize.xl};
    letter-spacing: 1px;
    margin-bottom: ${theme.spacing.lg};
    word-break: break-word;
    hyphens: auto;
  }
`;
export const PageSubtitle = styled.h2 `
  font-family: 'Adamina', serif;
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.normal};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.lg};
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.fontSize.xl};
  }
`;
export const SectionTitle = styled.h3 `
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.accent};
  margin-bottom: ${theme.spacing.md};
  text-transform: uppercase;
  letter-spacing: 1px;
`;
export const ContentSection = styled.section `
  margin-bottom: ${theme.spacing['2xl']};
  padding: ${theme.spacing.xl};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 0;
  position: relative;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${theme.colors.accent}, transparent);
    opacity: 0.4;
  }
`;
export const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 2}, 1fr);
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;
export const Card = styled.div `
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 0;
  padding: ${theme.spacing.lg};
  transition: all ${theme.effects.transition.luxury};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: ${theme.colors.accent};
    transition: left ${theme.effects.transition.normal};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.effects.boxShadow.lg};
    border-color: ${theme.colors.border.accent};

    &::before {
      left: 0;
    }
  }
`;
export const Text = styled.p `
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.normal};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight};
  margin-bottom: ${theme.spacing.md};
`;
export const HighlightText = styled.span `
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.semibold};
`;
export const Button = styled.button `
  background: ${props => props.variant === 'secondary' ? 'transparent' : theme.colors.text.primary};
  color: ${props => props.variant === 'secondary' ? theme.colors.text.primary : theme.colors.text.inverse};
  border: ${props => props.variant === 'secondary' ? `1px solid ${theme.colors.text.primary}` : `1px solid ${theme.colors.text.primary}`};
  border-radius: 0;
  padding: 12px 40px;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  cursor: pointer;
  transition: all ${theme.effects.transition.luxury};
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.variant === 'primary' ? '0 3px 10px rgba(62, 120, 173, 0.3)' : 'none'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${props => props.variant === 'secondary' ?
    theme.colors.text.primary :
    theme.colors.text.secondary};
    transition: left ${theme.effects.transition.normal};
    z-index: -1;
  }

  &:hover {
    background: ${props => props.variant === 'secondary' ? theme.colors.text.primary : theme.colors.text.secondary};
    color: ${theme.colors.text.inverse};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);

    &::before {
      left: 0;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const Input = styled.input `
  width: 100%;
  padding: ${theme.spacing.md};
  background: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 0;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.effects.transition.fast};

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
    box-shadow: 0 0 0 1px ${theme.colors.accent};
  }
`;
export const Textarea = styled.textarea `
  width: 100%;
  min-height: 120px;
  padding: ${theme.spacing.md};
  background: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 0;
  color: ${theme.colors.text.primary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.effects.transition.fast};
  resize: vertical;

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
    box-shadow: 0 0 0 1px ${theme.colors.accent};
  }
`;
export const Label = styled.label `
  display: block;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
  text-transform: uppercase;
  letter-spacing: 1px;
`;
export const FormGroup = styled.div `
  margin-bottom: ${theme.spacing.lg};
`;
export const Badge = styled.span `
  display: inline-block;
  padding: 6px 16px;
  background: ${props => {
    switch (props.color) {
        case 'success': return theme.colors.success;
        case 'error': return theme.colors.error;
        default: return theme.colors.primary; // Black background
    }
}};
  color: ${props => {
    switch (props.color) {
        case 'success': return theme.colors.text.white;
        case 'error': return theme.colors.text.white;
        default: return theme.colors.text.white; // White text
    }
}};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-right: ${theme.spacing.sm};
  border-radius: 0;
  border: 1px solid ${props => {
    switch (props.color) {
        case 'success': return theme.colors.success;
        case 'error': return theme.colors.error;
        default: return theme.colors.text.primary; // White border
    }
}};
  box-shadow: 0 2px 8px ${props => {
    switch (props.color) {
        case 'success': return 'rgba(34, 197, 94, 0.3)';
        case 'error': return 'rgba(239, 68, 68, 0.3)';
        default: return 'rgba(62, 120, 173, 0.3)';
    }
}};
`;
export { theme };
